import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MailCheck, RefreshCw, MousePointerClick, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthShell } from "@/components/nadia/AuthShell";
import { useAuth } from "@/lib/auth";
import { trackRegistration } from "@/lib/tracking";
import { authVerifyEmail, authResendVerification, authVerifyMock, goToCheckout, formatApiError } from "@/lib/api";

/** Only screen an unverified account can use. Real SMTP link: single-use, 48h, signs the holder in on click. */
export default function VerificaEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user, refresh, login, logout } = useAuth();
  const [busy, setBusy] = useState(null);
  const [linkState, setLinkState] = useState(token ? "verifying" : null);
  const [linkError, setLinkError] = useState(null);
  const [sendError, setSendError] = useState(null);
  const nav = useNavigate();

  const continueAfterVerify = async (verifiedAccount) => {
    const fresh = await refresh();
    // Plan chosen at registration is persisted on the account (survives opening the link from the mailbox); sessionStorage is only a fallback.
    const persisted = verifiedAccount?.pending_checkout || fresh?.pending_checkout || user?.pending_checkout;
    const session = JSON.parse(sessionStorage.getItem("nadia_pending") || "null");
    sessionStorage.removeItem("nadia_pending");
    const plan = persisted?.item_id || session?.plan;
    try {
      if (plan === "start" || plan === "pro" || plan === "flex") {
        toast(`Ti porto al pagamento del piano ${plan.charAt(0).toUpperCase() + plan.slice(1)}…`, { duration: 4000 });
        return await goToCheckout(nav, "subscription", plan);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
    nav("/app", { replace: true });
  };

  useEffect(() => {
    if (!token) return;
    authVerifyEmail(token)
      .then(async (r) => {
        trackRegistration(r?.event_id);
        if (r?.token) login(r.token, r.account);
        setLinkState("ok");
        toast.success("Email verificata: il tuo account è attivo");
        await continueAfterVerify(r?.account);
      })
      .catch((e) => { setLinkState("error"); setLinkError(formatApiError(e, "Link di verifica non valido, scaduto o già usato. Richiedi un nuovo link.")); });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user === false && !token) nav("/accedi", { replace: true });
    if (user && user.email_verified && linkState !== "verifying") nav("/app", { replace: true });
  }, [user, token, nav, linkState]);

  const resend = async () => {
    setBusy("resend"); setSendError(null);
    try { const r = await authResendVerification(); toast.success(r.message); await refresh(); }
    catch (e) { const msg = formatApiError(e); setSendError(msg); toast.error(msg); } finally { setBusy(null); }
  };
  const lastSend = user?.verification_email;
  const sendFailed = sendError || (lastSend && lastSend.ok === false ? `Invio email non riuscito: ${lastSend.error}` : null);
  const mock = async () => {
    setBusy("mock");
    try { await authVerifyMock(); toast.success("Email verificata: il tuo account è attivo"); await continueAfterVerify(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  return (
    <AuthShell
      eyebrow="Verifica email"
      title="Controlla la tua email"
      subtitle={linkState === "verifying" ? "Sto verificando il tuo link…" : linkState === "error" ? "Il link non è più valido." : user ? (sendFailed ? `Non sono riuscita a inviare l'email a ${user.email}.` : `Ho inviato un link di verifica a ${user.email}. Finché non lo confermi, l'account resta in attesa.`) : "Verifica email"}
      footer={<>Email sbagliata? <button onClick={async () => { await logout(); nav("/registrati"); }} className="text-cyan-300 hover:text-cyan-200" data-testid="verify-logout">Esci e registrati di nuovo</button> · <Link to="/" className="text-slate-300 hover:text-slate-100">Home</Link></>}
    >
      <div className="space-y-4" data-testid="verify-email-page">
        {linkState === "verifying" && (
          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3 text-sm text-slate-200" data-testid="verify-link-verifying"><Loader2 size={18} className="animate-spin text-cyan-300 shrink-0" /> Verifica del link in corso…</div>
        )}
        {linkState === "ok" && (
          <div className="flex items-center gap-3 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] p-3 text-sm text-emerald-100" data-testid="verify-link-ok"><CheckCircle2 size={18} className="text-emerald-300 shrink-0" /> Email verificata: ti sto portando in Nadia…</div>
        )}
        {linkState === "error" && (
          <div className="flex items-start gap-3 rounded-xl border border-rose-500/30 bg-rose-500/[0.06] p-3 text-sm text-rose-100" data-testid="verify-link-error"><AlertTriangle size={18} className="text-rose-300 shrink-0 mt-0.5" /> <span>{linkError}{!user && <> Accedi per richiedere un nuovo link: <Link to="/accedi" className="underline">Accedi</Link>.</>}</span></div>
        )}
        {linkState !== "ok" && (
          <div className="flex items-center gap-3 rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-3 text-sm text-amber-100" data-testid="verify-status">
            <MailCheck size={18} className="text-amber-300 shrink-0" /> Stato account: <span className="font-semibold">email non verificata</span>
          </div>
        )}
        {sendFailed && linkState !== "ok" && (
          <div className="flex items-start gap-3 rounded-xl border border-rose-500/30 bg-rose-500/[0.06] p-3 text-sm text-rose-100" data-testid="verify-send-error"><AlertTriangle size={18} className="text-rose-300 shrink-0 mt-0.5" /> <span>{sendFailed}. Riprova con "Invia di nuovo il link" o contatta support@nadiacloud.it.</span></div>
        )}
        <p className="text-sm text-slate-400">Dashboard, wallet e funzioni di Nadia si sbloccano appena confermi l'email. Nessun credito viene addebitato prima.</p>
        <Button onClick={resend} disabled={!!busy || !user} variant="outline" className="w-full h-11 border-white/10 bg-transparent text-slate-100 hover:border-cyan-400/50 hover:text-cyan-200 rounded-xl" data-testid="verify-resend">
          {busy === "resend" ? <Loader2 size={15} className="mr-2 animate-spin" /> : <RefreshCw size={15} className="mr-2" />} Invia di nuovo il link
        </Button>
        {user?.email_mode === "mock" && linkState !== "ok" && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2" data-testid="verify-mock-box">
            <div className="text-xs text-slate-400">Ambiente di anteprima senza server email: conferma qui al posto del link ricevuto.</div>
            <Button onClick={mock} disabled={!!busy} className="w-full h-11 bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 font-semibold rounded-xl" data-testid="verify-mock-button">
              {busy === "mock" ? <Loader2 size={15} className="mr-2 animate-spin" /> : <MousePointerClick size={15} className="mr-2" />} Conferma email (anteprima)
            </Button>
          </div>
        )}
      </div>
    </AuthShell>
  );
}
